'use client';

import { useCallback, useEffect } from 'react';
import type { Session } from '@fanduel/cpe-um-bundle/account/contract';
import type { Account } from '@fanduel/cpe-um-bundle/account/library';
import { useStore } from '@/store/store';
import { POPUPS } from '@/app/components/popup/constants';
import { getCookieValue, removeCookie, setCookie } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies';
import { UNIFIED_MODULES_ROUTES } from '@/utils/constants';
import { useRouter } from 'next/navigation';
import { getPlatformBridge } from '@/logic/bridges';
import { removeSession } from '@/logic/login/session';
import { getConfig } from '@/utils/config/config';
import { useSessionKeepAlive } from '@/hooks/use-session-keepalive';
import { ddClearUser, ddRumAction, ddSetUser } from '@/utils/datadog';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getPathFromReferrer } from '@/utils/url/url';
import { getPostLoginAction } from '@/utils/post-login-action/post-login-action';
import { jwtDecode } from 'jwt-decode';
import { sendSessionUpdateToGameSessions } from '@/utils/message-broker/send-to-game-session';

type TriggerActionOptions = { externalReferrer?: string; skipRegionSelector?: boolean };

type UseAccountUmOptions = {
    keepSessionAliveOnActivity?: boolean;
};

let initialized: boolean = false;
let initializationPromise: Promise<void> | null = null;

let accountManager: typeof Account | null = null;

export const SESSION_TYPE = {
    LOGIN: 'login',
    REGISTER: 'register'
};

export const SESSION_PHASE = {
    INIT: 'init',
    REFRESH: 'refresh'
} as const;

export type SessionPhase = (typeof SESSION_PHASE)[keyof typeof SESSION_PHASE];

export const CLEAR_SESSION_REASON = {
    EXPIRY_TIMER: 'expiry_timer',
    ACCOUNT_401: '401_within_account',
    OTHER: 'other'
} as const;

export type ClearSessionReason = (typeof CLEAR_SESSION_REASON)[keyof typeof CLEAR_SESSION_REASON];

export const sessionCreatedToStoreValue = (created: unknown): string | null => {
    if (created instanceof Date) {
        return created.toISOString();
    }
    if (typeof created === 'string') {
        return created;
    }

    return null;
};

export const useAccountUm = ({ keepSessionAliveOnActivity = true }: UseAccountUmOptions = {}) => {
    const queuePopup = useStore((s) => s.popup.queuePopup);
    const setIsLoggedIn = useStore((s) => s.userContext.setIsLoggedIn);
    const setBalance = useStore((s) => s.userContext.setBalance);
    const setLastLogin = useStore((state) => state.userContext.session.setLastLogin);
    const setSessionStartTime = useStore((state) => state.userContext.session.setSessionStartTime);
    const setAccountID = useStore((state) => state.userContext.accountSettings.setAccountID);
    const setUserName = useStore((state) => state.userContext.accountSettings.setUserName);
    const setEmailAddress = useStore((state) => state.userContext.accountSettings.setEmailAddress);
    const setInitCompleted = useStore((state) => state.userContext.session.setInitCompleted);
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const handleShowLoading = useStore((state) => state.loading.handleShowLoading);
    const handleHideLoading = useStore((state) => state.loading.handleHideLoading);
    const { isAndroidWrapper, isXsellWrapper } = useGlobalContext();
    const router = useRouter();

    const ensureInitialization = async () => {
        if (initializationPromise && !initialized) {
            handleShowLoading();
            await initializationPromise;
            handleHideLoading();
        }
    };

    // Getters
    const getSession = async () => {
        return accountManager?.getSessionAsync('Silent');
    };

    const getSessionWithLoginView = async () => {
        await ensureInitialization();

        return accountManager!.getSessionAsync('Normal');
    };

    const getSessionWithRegisterView = async () => {
        await ensureInitialization();

        return accountManager!.getSessionAsync('NewUser');
    };

    const isSessionValid = async () => {
        if (initializationPromise && !initialized) {
            await initializationPromise;
        }

        return accountManager?.checkValidSession();
    };

    const getUser = async () => accountManager?.getUserAsync('Silent');

    // Logout
    const logOut = async (reason: ClearSessionReason = CLEAR_SESSION_REASON.OTHER) => {
        await Promise.resolve(accountManager?.clearSession(undefined, reason));
        removeSession();
        setSessionStartTime(null);
        setBalance(0);
        setAccountID('');
        setUserName('');
        setEmailAddress('');
        ddClearUser();
    };

    const logOutWithModal = async (reason: ClearSessionReason = CLEAR_SESSION_REASON.OTHER) => {
        queuePopup({ type: POPUPS.LOGOUT });
        await logOut(reason);
    };

    const triggerLogOut = async (reason: ClearSessionReason = CLEAR_SESSION_REASON.OTHER) => {
        removeCookie(COOKIES.REGION);
        removeCookie(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR);
        removeCookie(COOKIES.X_GEOLOCATION_TOKEN);

        const bridge = getPlatformBridge();
        if ((isAndroidWrapper || isXsellWrapper) && bridge.isCompatible()) {
            bridge.exec('logout');
            await logOut(reason);
        } else {
            await logOutWithModal(reason);
        }
    };

    // Session handling
    const syncCookiesUM = async (session: Session) => {
        const config = getConfig();
        const loginToken = await accountManager!.getLoginTokenAsync();
        const { token, expires } = session;

        // TODO: There is an issue regarding expires not accounting for user current timezone
        if (loginToken) {
            setCookie(COOKIES.AUTH_TOKEN, token, expires.valueOf() - Date.now(), '/', config.rootDomain);
            setCookie(COOKIES.LOGIN_TOKEN, loginToken, expires.valueOf() - Date.now(), '/', config.rootDomain);
        }
    };

    const keepSessionAlive = useCallback(async () => {
        return accountManager?.keepAlive();
    }, []);

    const setDDUser = (token: string) => {
        const decodedToken = jwtDecode(token);

        if (decodedToken.sub) {
            ddSetUser(decodedToken.sub);
        }
    };

    const handleSession = async (session: Session | undefined, context: { phase: SessionPhase }) => {
        const loggedIn = useStore.getState().userContext.isLoggedIn;
        if (session) {
            setSessionStartTime(sessionCreatedToStoreValue(session.created));

            const user = await getUser();

            if (user?.userId) {
                setAccountID(user?.userId);
            }

            ddRumAction({
                name: 'session_extended',
                description: 'Session was extended successfully.',
                context: {
                    ...context,
                    sessionId: session.sessionId,
                    expires: session.expires
                },
                use_case: 'SESSION'
            });
            await syncCookiesUM(session);
            const lastLoginDate = (await accountManager?.getPriorSession?.())?.created;
            setLastLogin(lastLoginDate || '');
            setDDUser(session.token);

            sendSessionUpdateToGameSessions(useStore.getState(), session.token);
            // On logged-out -> logged-in with a pending CTA action, reload once after cookie sync
            // so the next boot consumes the action from a fully logged-in page.
            if (
                !loggedIn &&
                context.phase === SESSION_PHASE.REFRESH &&
                getPostLoginAction() !== null &&
                !isAndroidWrapper &&
                !isXsellWrapper
            ) {
                window.location.reload();
                return;
            }
        } else if (loggedIn) {
            //TODO never check  AUTH_TOKEN...
            // Only display if the user was logged in before
            ddRumAction({
                name: 'session_invalidated',
                description: 'Session was invalidated.',
                context,
                use_case: 'SESSION'
            });
            triggerLogOut(CLEAR_SESSION_REASON.ACCOUNT_401);
        }
        setIsLoggedIn(Boolean(session));
    };

    // Register
    const buildRefParam = (ref?: string) => (ref ? `?external-referrer-next=${encodeURIComponent(ref)}` : '');

    const triggerRegisterWeb = async (options?: TriggerActionOptions) => {
        const shouldShowRegionSelector =
            getCookieValue(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR) === 'true' && !options?.skipRegionSelector;
        if (shouldShowRegionSelector) {
            queuePopup({
                type: POPUPS.STATE_SELECTOR,
                params: {
                    type: SESSION_TYPE.REGISTER,
                    externalReferrer: options?.externalReferrer
                }
            });
        } else {
            // Read before login completes: post-login action handler clears sessionStorage once logged in.
            const hasPendingPostLoginAction = Boolean(getPostLoginAction());
            const session = await accountManager?.getSessionAsync('NewUser');
            if (!session) {
                return;
            }
            if (hasPendingPostLoginAction) {
                return;
            }
            const path = getPathFromReferrer(options?.externalReferrer);
            if (path) {
                router.push(path);
            }
            router.refresh();
        }
    };

    const triggerRegister = (options?: TriggerActionOptions) => {
        if (isAndroidWrapper) {
            window.location.href = `${UNIFIED_MODULES_ROUTES.REGISTER}${buildRefParam(options?.externalReferrer)}`;
        } else if (isXsellWrapper) {
            const bridge = getPlatformBridge();
            if (bridge.isCompatible()) {
                bridge.exec('join');
            } else {
                triggerRegisterWeb(options);
            }
        } else {
            triggerRegisterWeb(options);
        }
    };

    // Login
    const triggerLoginWeb = async (options?: TriggerActionOptions): Promise<Session | undefined> => {
        const shouldShowRegionSelector =
            getCookieValue(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR) === 'true' && !options?.skipRegionSelector;
        if (shouldShowRegionSelector) {
            queuePopup({
                type: POPUPS.STATE_SELECTOR,
                params: {
                    type: SESSION_TYPE.LOGIN,
                    externalReferrer: options?.externalReferrer
                }
            });
            return undefined;
        }

        // Read before login completes: post-login action handler clears sessionStorage once logged in.
        const hasPendingPostLoginAction = Boolean(getPostLoginAction());
        const session = await getSessionWithLoginView();
        if (!session) {
            return undefined;
        }

        ddRumAction({
            name: 'login_success',
            description: 'User logged in successfully.',
            use_case: 'SESSION'
        });
        await syncCookiesUM(session);

        if (hasPendingPostLoginAction) {
            return session;
        }

        const path = getPathFromReferrer(options?.externalReferrer);
        if (path) {
            router.push(path);
        }
        router.refresh();

        return session;
    };

    const triggerLogin = (options?: TriggerActionOptions): Promise<Session | undefined> | undefined => {
        if (isAndroidWrapper) {
            window.location.href = `${UNIFIED_MODULES_ROUTES.LOGIN}${buildRefParam(options?.externalReferrer)}`;
            return undefined;
        } else if (isXsellWrapper) {
            const bridge = getPlatformBridge();
            if (bridge.isCompatible()) {
                bridge.exec('login');
                return undefined;
            }
        }
        return triggerLoginWeb(options);
    };

    const refreshSession = async (): Promise<Session | undefined> => {
        if (initializationPromise && !initialized) {
            await initializationPromise;
        }

        const session = await accountManager?.getSessionAsync('Silent');

        if (session) {
            await syncCookiesUM(session);
        }

        return session ?? undefined;
    };

    const reauthenticate = async (): Promise<Session | undefined> => {
        let session: Session | undefined;

        try {
            session = await refreshSession();
        } catch {
            session = undefined;
        }

        if (!session) {
            return (await triggerLogin()) ?? undefined;
        }

        return session;
    };

    // Initialize
    const initializeAccountUM = async () => {
        if (initialized) {
            return;
        }

        if (initializationPromise) {
            await initializationPromise;
            return;
        }

        initializationPromise = (async () => {
            accountManager = (await import('@fanduel/cpe-um-bundle/account/library')).Account;

            const { initializeUnifiedModules } = await import('@/logic/unified-modules');
            await initializeUnifiedModules();

            const loginToken = getCookieValue(COOKIES.LOGIN_TOKEN);
            let session = undefined;

            if (loginToken) {
                session = await accountManager?.getSessionAsync({ loginToken });
            }

            await handleSession(session, {
                phase: SESSION_PHASE.INIT
            });

            setInitCompleted(true);

            await accountManager.subscribeSession((val) =>
                handleSession(val, {
                    phase: SESSION_PHASE.REFRESH
                })
            );

            initialized = true;
        })();

        await initializationPromise;
    };

    useEffect(() => {
        initializeAccountUM();
    }, []);

    useSessionKeepAlive({
        onActivity: keepSessionAlive,
        enabled: keepSessionAliveOnActivity
    });

    return {
        getSession,
        refreshSession,
        reauthenticate,
        getSessionWithLoginView,
        getSessionWithRegisterView,
        isSessionValid,
        keepSessionAlive,
        triggerLogin,
        triggerRegister,
        triggerLogOut,
        logOut,
        logOutWithModal,
        getUser,
        isLoggedIn
    };
};
